import * as React from 'react';

import { DEFAULT_SKILL_OPTIONS_LIMIT } from 'bundles/onboarding-common/constants';
import ChipAndAutocompleteInput from 'bundles/personalization-settings/components/ChipAndAutocompleteInput';
import {
  useDebouncedPersonalizationSearchTracker,
  useTrackPersonalizationButtonClick,
  useTrackPersonalizationSelectChange,
} from 'bundles/personalization-settings/hooks/usePersonalizationEventTracking';
import useSkillsEditor, { CREATE_USER_GENERATED_KEY } from 'bundles/personalization-settings/hooks/useSkillsEditor';
import type { AvailableSkill } from 'bundles/personalization-settings/hooks/useSkillsEditor';
import { removeSkill } from 'bundles/personalization-settings/utils/skillsWantedV2';
import type { SelectedSkill } from 'bundles/personalization-settings/utils/skillsWantedV2';

import _t from 'i18n!nls/personalization-settings';

export type SkillsEditorProps = {
  /** The learner's currently selected desired skills (controlled). */
  value: SelectedSkill[];
  /** Called with the next selection whenever a skill is added or removed. */
  onChange: (skills: SelectedSkill[]) => void;
  /** Catalog skills the learner can pick from, supplied by the parent container. */
  availableSkills?: readonly AvailableSkill[];
  /**
   * Called with the current typeahead text as the learner types. The container
   * uses it to load matching catalog skills into `availableSkills`.
   */
  onInputChange?: (input: string) => void;
  /** True while the container is loading `availableSkills`, so the popover can show a loading state. */
  loading?: boolean;
  /** Called when the learner tries to add a skill while already at the limit. */
  onLimitExceededAttempt?: () => void;
  /** Called when the input loses focus, e.g. to dismiss a limit-exceeded error. */
  onBlur?: () => void;
};

const chipKey = (skill: SelectedSkill) => (skill.type === 'catalog' ? `catalog:${skill.id}` : `user:${skill.name}`);

/**
 * The “Desired skills” editor for the Personalization settings page: a chip
 * group of the learner's selected skills plus a typeahead to add more. The
 * catalog of selectable skills is supplied by the parent via `availableSkills`;
 * a learner can also add a free-text (user-generated) skill that isn't in it.
 *
 * Controlled via `value`/`onChange`. It does not read or persist the Learner
 * Object itself — the page wires the loaded `skills_wanted_v2` value in (LO-4743)
 * and fires the update mutation on Save (LO-4744).
 */
const SkillsEditor = ({
  value,
  onChange,
  availableSkills = [],
  onInputChange,
  loading,
  onLimitExceededAttempt,
  onBlur,
}: SkillsEditorProps) => {
  // Mirrors ChipAndAutocompleteInput's internal input so useSkillsEditor can
  // compute the right options as the learner types.
  const [input, setInput] = React.useState('');

  const { options, addSkillFromOption, addSkillFromInput } = useSkillsEditor({
    value,
    onChange,
    availableSkills,
    input,
  });

  const trackSearchInput = useDebouncedPersonalizationSearchTracker('skills_search');
  const trackButtonClick = useTrackPersonalizationButtonClick();
  const trackSelectChange = useTrackPersonalizationSelectChange();

  const handleInputChange = (next: string) => {
    setInput(next);
    onInputChange?.(next);
    trackSearchInput(next);
  };

  const chips = value.map((skill) => ({ key: chipKey(skill), label: skill.name }));
  const chipKeyToSkill = new Map(value.map((skill) => [chipKey(skill), skill]));

  const handleSelectOption = (key: string) => {
    const nextValue = addSkillFromOption(key);
    if (nextValue) {
      trackButtonClick('personalization_settings_add_skill');
      trackSelectChange(
        'skills',
        nextValue.map((skill) => skill.name)
      );
    }
  };

  const handleAddFromInput = (trimmedInput: string) => {
    const nextValue = addSkillFromInput(trimmedInput);
    if (nextValue) {
      trackButtonClick('personalization_settings_add_skill');
      trackSelectChange(
        'skills',
        nextValue.map((skill) => skill.name)
      );
    }
  };

  const handleRemoveChip = (key: string) => {
    const skill = chipKeyToSkill.get(key);
    if (!skill) return;
    const nextValue = removeSkill(value, skill);
    onChange(nextValue);
    trackButtonClick('personalization_settings_remove_skill');
    trackSelectChange(
      'skills',
      nextValue.map((s) => s.name)
    );
  };

  const displayOptions = options.map((o) =>
    o.key === CREATE_USER_GENERATED_KEY
      ? { key: o.key, label: _t('Add “#{label}”', { label: o.label }) }
      : { key: o.key, label: o.label }
  );

  return (
    <ChipAndAutocompleteInput
      chips={chips}
      onRemoveChip={handleRemoveChip}
      options={displayOptions}
      onSelectOption={handleSelectOption}
      onAddFromInput={handleAddFromInput}
      onInputChange={handleInputChange}
      loading={loading}
      labels={{
        chipsGroup: _t('Selected skills'),
        inputPlaceholder: _t('Add a skill'),
        addButton: _t('Add skill'),
        notFound: _t('No skills found'),
      }}
      limit={DEFAULT_SKILL_OPTIONS_LIMIT}
      onLimitExceededAttempt={onLimitExceededAttempt}
      onInputBlur={onBlur}
    />
  );
};

export default SkillsEditor;
